/*
 * Copyright 2024 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import {ActionButtonContext} from './ActionButton';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuItemProps as AriaMenuItemProps,
  MenuLoadMoreItem as AriaMenuLoadMoreItem,
  MenuProps as AriaMenuProps,
  MenuSection as AriaMenuSection,
  MenuSectionProps as AriaMenuSectionProps,
  MenuTrigger as AriaMenuTrigger,
  MenuTriggerProps as AriaMenuTriggerProps,
  SubmenuTrigger as AriaSubmenuTrigger,
  SubmenuTriggerProps as AriaSubmenuTriggerProps,
  MenuItemRenderProps,
  MenuStateContext
} from 'react-aria-components/Menu';
import {
  AsyncLoadable,
  DOMRef,
  DOMRefValue,
  GlobalDOMAttributes,
  LoadingState,
  Node,
  PressEvent
} from '@react-types/shared';
import {
  baseColor,
  centerPadding,
  focusRing,
  fontRelative,
  size,
  space,
  style
} from '../style' with {type: 'macro'};
import {box, iconStyles} from './Checkbox';
import {centerBaseline} from './CenterBaseline';
import CheckmarkIcon from '../ui-icons/Checkmark';
import ChevronRightIcon from '../ui-icons/Chevron';
import {Collection} from 'react-aria/Collection';
import {
  CollectionRendererContext,
  DefaultCollectionRenderer
} from 'react-aria-components/CollectionBuilder';
import {ContextValue, DEFAULT_SLOT, Provider, useSlottedContext} from 'react-aria-components/slots';
import {
  control,
  controlFont,
  controlSize,
  getAllowedOverrides,
  StyleProps
} from './style-utils' with {type: 'macro'};
import {
  createContext,
  ForwardedRef,
  forwardRef,
  JSX,
  ReactElement,
  ReactNode,
  useContext,
  useRef,
  useState
} from 'react';
import {createLeafComponent} from 'react-aria/CollectionBuilder';
import {divider} from './Divider';
import {edgeToText} from '../style/spectrum-theme' with {type: 'macro'};
import {forwardRefType} from './types';
import {HeaderContext, HeadingContext, KeyboardContext, Text, TextContext} from './Content';
import {IconContext} from './Icon';
import {ImageContext} from './Image';
import InfoCircleIcon from '../s2wf-icons/S2_Icon_InfoCircle_20_N.svg'; // chevron right removed??
import {InPopoverContext, Popover, PopoverContext} from './Popover';
import intlMessages from '../intl/*.json';
import {isSeparatorHidden, SeparatorNode} from './separator-utils';
import LinkOutIcon from '../ui-icons/LinkOut';
import {ListLayout, Virtualizer} from 'react-aria-components/Virtualizer';
import {mergeStyles} from '../style/runtime';
import {Placement} from 'react-aria/useOverlayPosition';
import {PressResponder} from 'react-aria/private/interactions/PressResponder';
import {pressScale} from './pressScale';
import {ProgressCircle} from './ProgressCircle';
import {Separator, SeparatorProps} from 'react-aria-components/Separator';
import {ToggleButtonContext} from './ToggleButton';
import {useGlobalListeners} from 'react-aria/private/utils/useGlobalListeners';
import {useId} from 'react-aria/useId';
import {useLocale} from 'react-aria/I18nProvider';
import {useLocalizedStringFormatter} from 'react-aria/useLocalizedStringFormatter';
import {useScale} from './utils';
import {useSpectrumContextProps} from './useSpectrumContextProps';
// viewbox on LinkOut is super weird just because i copied the icon from designs...
// need to strip id's from icons

export interface MenuTriggerProps extends AriaMenuTriggerProps {
  /**
   * Alignment of the menu relative to the trigger.
   *
   * @default 'start'
   */
  align?: 'start' | 'end';
  /**
   * Where the Menu opens relative to its trigger.
   *
   * @default 'bottom'
   */
  direction?: 'bottom' | 'top' | 'left' | 'right' | 'start' | 'end';
  /**
   * Whether the menu should automatically flip direction when space is limited.
   *
   * @default true
   */
  shouldFlip?: boolean;
}

export interface MenuProps<T>
  extends
    Omit<
      AriaMenuProps<T>,
      'children' | 'style' | 'className' | 'render' | 'renderEmptyState' | keyof GlobalDOMAttributes
    >,
    Pick<AsyncLoadable, 'onLoadMore'>,
    StyleProps {
  /**
   * The size of the Menu.
   *
   * @default 'M'
   */
  size?: 'S' | 'M' | 'L' | 'XL';
  /**
   * The contents of the collection.
   */
  children: ReactNode | ((item: T) => ReactNode);
  /** Hides the default link out icons on menu items that open links in a new tab. */
  hideLinkOutIcon?: boolean;
  /**
   * The current loading state of the Menu.
   */
  loadingState?: LoadingState;
  /**
   * Whether the Menu should be virtualized. Submenus inherit this from their parent menu by
   * default.
   *
   * @default false
   */
  isVirtualized?: boolean;
}

export const MenuContext =
  createContext<ContextValue<Partial<MenuProps<any>>, DOMRefValue<HTMLDivElement>>>(null);

const menuItemGrid = {
  size: {
    S: [edgeToText(24), 'auto', 'auto', 'minmax(0, 1fr)', 'auto', 'auto', 'auto', edgeToText(24)],
    M: [edgeToText(32), 'auto', 'auto', 'minmax(0, 1fr)', 'auto', 'auto', 'auto', edgeToText(32)],
    L: [edgeToText(40), 'auto', 'auto', 'minmax(0, 1fr)', 'auto', 'auto', 'auto', edgeToText(40)],
    XL: [edgeToText(48), 'auto', 'auto', 'minmax(0, 1fr)', 'auto', 'auto', 'auto', edgeToText(48)]
  }
} as const;

// TODO: this is baiscally Picker's "menu" styling now, but with maxWidths and stuff
export let menu = style(
  {
    outlineStyle: 'none',
    display: 'grid',
    gridTemplateColumns: menuItemGrid,
    boxSizing: 'border-box',
    maxHeight: 'inherit',
    width: 'full',
    overflow: {
      isPopover: 'auto',
      // similar to Combobox/Picker, needs this for virtualized so we get scroll events for virtualizer
      isVirtualized: 'auto'
    },
    maxWidth: {
      isPopover: 320
    },
    padding: {
      isPopover: {
        default: 8,
        isVirtualized: 0
      }
    },
    fontFamily: 'sans',
    fontSize: controlFont(),
    gridAutoRows: 'min-content'
  },
  getAllowedOverrides()
);

const virtualizedMenuWidth = style<{isVirtualized?: boolean}>({
  width: {
    isVirtualized: 150
  }
});

export let section = style({
  gridColumnStart: 1,
  gridColumnEnd: -1,
  alignItems: 'center',
  display: 'grid',
  gridTemplateAreas: [
    '. checkmark icon label       value keyboard descriptor .',
    '. .         .    description .     .        .          .'
  ],
  gridTemplateColumns: menuItemGrid
});

export let sectionHeader = style<{size?: 'S' | 'M' | 'L' | 'XL'; isVirtualized?: boolean}>({
  color: 'neutral',
  gridColumnStart: 2,
  gridColumnEnd: -2,
  // also from Combobox/Picker, but we want to keep the non virtualized menu styling too
  marginX: {
    isVirtualized: {
      size: {
        S: `[${edgeToText(24)}]`,
        M: `[${edgeToText(32)}]`,
        L: `[${edgeToText(40)}]`,
        XL: `[${edgeToText(48)}]`
      }
    }
  },
  boxSizing: 'border-box',
  minHeight: controlSize(),
  paddingY: centerPadding()
});

export let sectionHeading = style({
  font: 'ui',
  fontWeight: 'bold',
  margin: 0
});

export let menuitem = style<
  Omit<MenuItemRenderProps, 'hasSubmenu' | 'isOpen'> & {
    isFocused: boolean;
    size: 'S' | 'M' | 'L' | 'XL';
    isLink?: boolean;
    hasSubmenu?: boolean;
    isOpen?: boolean;
    isVirtualized?: boolean;
  }
>(
  {
    ...focusRing(),
    ...control({shape: 'default', wrap: true, icon: true}),
    columnGap: 0,
    paddingX: 0,
    paddingBottom: '--labelPadding',
    backgroundColor: {
      // TODO: revisit color when I have access to dev mode again
      default: {
        default: 'transparent',
        isFocused: {
          default: baseColor('gray-100').isFocusVisible,
          forcedColors: 'Highlight'
        }
      }
    },
    color: {
      default: baseColor('neutral'),
      isDisabled: 'disabled',
      forcedColors: {
        default: 'ButtonText',
        isFocused: 'HighlightText',
        isDisabled: 'GrayText'
      }
    },
    position: 'relative',
    // each menu item should take up the entire width, the subgrid will handle within the item
    gridColumnStart: 1,
    gridColumnEnd: -1,
    display: 'grid',
    gridTemplateAreas: [
      '. checkmark icon label       value keyboard descriptor .',
      '. .         .    description .     .        .          .'
    ],
    gridTemplateColumns: {
      default: 'subgrid',
      // cant use subgrid since virtualizer div wrapper
      isVirtualized: menuItemGrid
    },
    gridTemplateRows: {
      // min-content prevents second row from 'auto'ing to a size larger then 0 when empty
      default: 'auto minmax(0, min-content)',
      ':has([slot=description])': 'auto auto'
    },
    rowGap: {
      ':has([slot=description])': space(1)
    },
    height: 'min',
    textDecoration: 'none',
    cursor: {
      default: 'default',
      isLink: 'pointer'
    },
    transition: 'transform',
    forcedColorAdjust: 'none'
  },
  getAllowedOverrides()
);

export let checkmark = style<{
  isSelected: boolean;
  isFocused: boolean;
  size: 'S' | 'M' | 'L' | 'XL';
}>({
  visibility: {
    default: 'hidden',
    isSelected: 'visible'
  },
  gridArea: 'checkmark',
  color: baseColor('accent'),
  '--iconPrimary': {
    type: 'fill',
    value: {
      default: 'currentColor',
      forcedColors: {
        default: 'Highlight',
        isFocused: 'HighlightText'
      }
    }
  },
  marginEnd: 'text-to-control',
  aspectRatio: 'square'
});

export let checkbox = style({
  gridArea: 'checkmark',
  marginEnd: 'text-to-control'
});

let hiddenCheckbox = style({
  visibility: 'hidden'
});

export let icon = style({
  display: 'block',
  size: '1lh',
  // too small default icon size is wrong, it's like the icons are 1 tshirt size bigger than the rest of the component? check again after typography changes
  // reminder, size of WF is applied via font size
  marginEnd: 'text-to-visual',
  '--iconPrimary': {
    type: 'fill',
    value: 'currentColor'
  }
});

export let iconCenterWrapper = style({
  display: 'flex',
  gridArea: 'icon'
});

let image = style({
  gridArea: 'icon',
  gridRowEnd: 'span 2',
  marginEnd: 'text-to-visual',
  marginTop: fontRelative(6), // made up, need feedback
  alignSelf: 'center',
  borderRadius: 'sm',
  size: {
    default: 40,
    size: {
      S: 32,
      M: 40,
      L: 44,
      XL: 48 // TODO: feedback, Why is it 50x50, that's on 12.25 so doesn't fit the grid at all
    }
  },
  aspectRatio: 'square',
  objectFit: 'contain'
});

export let label = style<{size: string}>({
  gridArea: 'label',
  font: controlFont(),
  color: 'inherit',
  fontWeight: 'medium',
  // TODO: token values for padding not defined yet, revisit
  marginTop: '--labelPadding'
});

export let description = style<{
  size: 'S' | 'M' | 'L' | 'XL';
  isFocused: boolean;
  isDisabled: boolean;
}>({
  gridArea: 'description',
  font: {
    default: 'ui-sm',
    size: {
      S: 'ui-xs',
      M: 'ui-sm',
      L: 'ui',
      XL: 'ui-lg'
    }
  },
  color: {
    default: baseColor('neutral-subdued'),
    // Ideally this would use the same token as hover, but we don't have access to that here.
    // TODO: should we always consider isHovered and isFocused to be the same thing?
    isFocused: 'gray-800',
    isDisabled: 'disabled',
    forcedColors: {
      default: 'inherit'
    }
  }
});

let value = style({
  gridArea: 'value',
  marginStart: 8
});

let keyboard = style<{size: 'S' | 'M' | 'L' | 'XL'; isDisabled: boolean; isFocused: boolean}>({
  gridArea: 'keyboard',
  marginStart: 8,
  font: 'ui',
  textAlign: 'end',
  color: {
    default: 'gray-600',
    isDisabled: 'disabled',
    forcedColors: {
      default: 'inherit'
    }
  },
  unicodeBidi: 'plaintext'
});

let descriptor = style({
  gridArea: 'descriptor',
  placeSelf: 'end',
  marginStart: 8,
  '--iconPrimary': {
    type: 'fill',
    value: 'currentColor'
  }
});

let descriptorIcon = style<{size: 'S' | 'M' | 'L' | 'XL'}>({
  marginEnd: 0,
  display: 'block',
  size: {
    size: {
      S: 16,
      M: 20,
      L: 24,
      XL: 26
    }
  }
});

let InternalMenuContext = createContext<{
  size: 'S' | 'M' | 'L' | 'XL';
  isSubmenu: boolean;
  hideLinkOutIcon: boolean;
  isVirtualized: boolean;
}>({
  size: 'M',
  isSubmenu: false,
  hideLinkOutIcon: false,
  isVirtualized: false
});

let InternalMenuTriggerContext = createContext<Omit<MenuTriggerProps, 'children'> | null>(null);
let UnavailableContext = createContext(false);

let wrappingDiv = style({
  display: 'flex',
  size: 'full'
});

export const loadingWrapperStyles = style({
  gridColumnStart: '1',
  gridColumnEnd: '-1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginY: 8
});

export const progressCircleStyles = style({
  size: '1lh'
});

const emptyStateText = style({
  height: {
    size: {
      S: 24,
      M: 32,
      L: 40,
      XL: 48
    }
  },
  font: {
    size: {
      S: 'ui-sm',
      M: 'ui',
      L: 'ui-lg',
      XL: 'ui-xl'
    }
  },
  display: 'flex',
  alignItems: 'center',
  paddingX: 'edge-to-text'
});

const ROW_HEIGHTS = {
  S: {
    medium: 24,
    large: 30
  },
  M: {
    medium: 32,
    large: 40
  },
  L: {
    medium: 40,
    large: 50
  },
  XL: {
    medium: 48,
    large: 60
  }
};

/**
 * Menus display a list of actions or options that a user can choose.
 */
export const Menu = /*#__PURE__*/ (forwardRef as forwardRefType)(function Menu<T>(
  props: MenuProps<T>,
  ref: DOMRef<HTMLDivElement>
) {
  [props, ref] = useSpectrumContextProps(props, ref, MenuContext);
  let {
    isSubmenu,
    size: ctxSize,
    isVirtualized: isParentVirtualized
  } = useContext(InternalMenuContext);
  let {
    children,
    size = ctxSize,
    UNSAFE_style,
    UNSAFE_className,
    styles,
    hideLinkOutIcon = false,
    items,
    loadingState,
    onLoadMore,
    isVirtualized = isParentVirtualized
  } = props;
  let ctx = useContext(InternalMenuTriggerContext);
  let inPopover = useContext(InPopoverContext);
  let stringFormatter = useLocalizedStringFormatter(intlMessages, '@react-spectrum/s2');
  let scale = useScale();

  let menuLoadingCircle = (
    <AriaMenuLoadMoreItem
      isLoading={loadingState === 'loadingMore'}
      onLoadMore={onLoadMore}
      className={loadingWrapperStyles}>
      <ProgressCircle
        isIndeterminate
        size="S"
        styles={progressCircleStyles}
        aria-label={stringFormatter.format('menu.loadingMore')}
      />
    </AriaMenuLoadMoreItem>
  );

  let renderer;
  if (typeof children === 'function' && items) {
    renderer = (
      <>
        <Collection items={items} dependencies={props.dependencies}>
          {children}
        </Collection>
        {menuLoadingCircle}
      </>
    );
  } else {
    renderer = (
      <>
        {children}
        {menuLoadingCircle}
      </>
    );
  }

  let isPopover = (ctx || isSubmenu) && !inPopover;
  let menuContent = (
    <AriaMenu
      {...props}
      className={menu(
        {size, isPopover, isVirtualized},
        isPopover
          ? null
          : mergeStyles(
              // if in user provided popover use their width instead of applying a min to the menu
              virtualizedMenuWidth({isVirtualized: isVirtualized && !inPopover}),
              styles
            )
      )}
      renderEmptyState={() =>
        loadingState === 'loading' ? (
          <div className={loadingWrapperStyles}>
            <ProgressCircle
              isIndeterminate
              size="S"
              styles={progressCircleStyles}
              aria-label={stringFormatter.format('menu.loading')}
            />
          </div>
        ) : (
          <span className={emptyStateText({size})}>{stringFormatter.format('menu.noResults')}</span>
        )
      }>
      {renderer}
    </AriaMenu>
  );

  let menuWithVirtualizer = isVirtualized ? (
    <Virtualizer
      layout={ListLayout}
      layoutOptions={{
        estimatedRowSize: ROW_HEIGHTS[size][scale],
        estimatedHeadingSize: ROW_HEIGHTS[size][scale],
        padding: 8
      }}>
      {menuContent}
    </Virtualizer>
  ) : isParentVirtualized ? (
    //  if user doesnt want their submenu to be virtualized we need to clear context from parent
    <CollectionRendererContext.Provider value={DefaultCollectionRenderer}>
      {menuContent}
    </CollectionRendererContext.Provider>
  ) : (
    menuContent
  );

  let content = (
    <InternalMenuContext.Provider value={{size, isSubmenu: true, hideLinkOutIcon, isVirtualized}}>
      <Provider
        values={[
          [HeaderContext, {styles: sectionHeader({size, isVirtualized})}],
          [
            HeadingContext,
            {
              // @ts-ignore
              role: 'presentation',
              styles: sectionHeading
            }
          ],
          [
            TextContext,
            {
              slots: {
                description: {styles: description({size, isFocused: false, isDisabled: false})}
              }
            }
          ],
          [InPopoverContext, false]
        ]}>
        {menuWithVirtualizer}
      </Provider>
    </InternalMenuContext.Provider>
  );

  if (isPopover) {
    return (
      <Popover ref={ref} padding="none" hideArrow>
        <div
          style={UNSAFE_style}
          className={
            (UNSAFE_className || '') +
            mergeStyles(wrappingDiv, virtualizedMenuWidth({isVirtualized}), styles)
          }>
          {content}
        </div>
      </Popover>
    );
  }

  return content;
});

let dividerPlacement = style({
  display: 'grid',
  gridColumnStart: 2,
  gridColumnEnd: -2,
  marginY: size(5)
});

// same approach as combobox, need a wrapper with a fixed height so virtualizer measures it properly
let virtualizedDividerWrapper = style<{size: 'S' | 'M' | 'L' | 'XL'}>({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: 12,
  marginX: {
    size: {
      S: `[${edgeToText(24)}]`,
      M: `[${edgeToText(32)}]`,
      L: `[${edgeToText(40)}]`,
      XL: `[${edgeToText(48)}]`
    }
  }
});

export const Divider = /*#__PURE__*/ createLeafComponent(
  SeparatorNode,
  function Divider(props: SeparatorProps, ref: ForwardedRef<HTMLElement>, node: Node<unknown>) {
    let state = useContext(MenuStateContext)!;
    let {size: ctxSize, isVirtualized} = useContext(InternalMenuContext);

    if (isSeparatorHidden(node, state.collection)) {
      return null;
    }

    let dividerStyles = divider({
      size: 'M',
      orientation: 'horizontal',
      isStaticColor: false
    });

    if (isVirtualized) {
      return (
        <div className={virtualizedDividerWrapper({size: ctxSize})}>
          <Separator {...props} ref={ref} className={dividerStyles} />
        </div>
      );
    }

    return (
      <Separator {...props} ref={ref} className={mergeStyles(dividerStyles, dividerPlacement)} />
    );
  }
);

export interface MenuSectionProps<T> extends Omit<
  AriaMenuSectionProps<T>,
  'style' | 'className' | 'render' | keyof GlobalDOMAttributes
> {}

export function MenuSection<T>(props: MenuSectionProps<T>): ReactNode {
  // remember, context doesn't work if it's around Section nor inside
  let {size} = useContext(InternalMenuContext);
  return (
    <>
      <AriaMenuSection {...props} className={section({size})}>
        {props.children}
      </AriaMenuSection>
      <Divider />
    </>
  );
}

export interface MenuItemProps
  extends
    Omit<
      AriaMenuItemProps,
      'children' | 'style' | 'className' | 'render' | 'onClick' | keyof GlobalDOMAttributes
    >,
    StyleProps {
  /**
   * The contents of the item.
   */
  children: ReactNode;
}

const checkmarkIconSize = {
  S: 'XS',
  M: 'M',
  L: 'L',
  XL: 'XL'
} as const;

const linkIconSize = {
  S: 'M',
  M: 'L',
  L: 'XL',
  XL: 'XL'
} as const;

interface UnavailableIconWrapperProps {
  direction: 'ltr' | 'rtl';
  size: 'S' | 'M' | 'L' | 'XL';
  id?: string;
}

function UnavailableIconWrapper(props: UnavailableIconWrapperProps) {
  let {direction, size, id} = props;
  let stringFormatter = useLocalizedStringFormatter(intlMessages, '@react-spectrum/s2');

  return (
    <div
      slot="descriptor"
      className={mergeStyles(descriptor, style({marginBottom: fontRelative(-1)}))}
      id={id}>
      <Provider values={[[IconContext, {slots: {icon: {styles: descriptorIcon({size})}}}]]}>
        <InfoCircleIcon
          aria-label={stringFormatter.format('menu.unavailable')}
          className={style({
            scaleX: {
              direction: {
                rtl: -1
              }
            }
          })({direction})}
        />
      </Provider>
    </div>
  );
}

export function MenuItem(props: MenuItemProps): ReactNode {
  let ref = useRef(null);
  let isLink = props.href != null;
  let isLinkOut = isLink && props.target === '_blank';
  let {size, hideLinkOutIcon, isVirtualized} = useContext(InternalMenuContext);
  let textValue =
    props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  let {direction} = useLocale();
  let isUnavailable = useContext(UnavailableContext);
  let infoIconId = useId();

  // oxlint-disable react/react-compiler
  return (
    <AriaMenuItem
      {...props}
      aria-describedby={isUnavailable ? infoIconId : undefined}
      textValue={textValue}
      ref={ref}
      style={pressScale(ref, props.UNSAFE_style)}
      className={renderProps =>
        (props.UNSAFE_className || '') +
        menuitem(
          {
            ...renderProps,
            isFocused: (renderProps.hasSubmenu && renderProps.isOpen) || renderProps.isFocused,
            size,
            isLink,
            isVirtualized
          },
          props.styles
        )
      }>
      {renderProps => {
        let {children} = props;
        let checkboxRenderProps = {
          ...renderProps,
          size,
          isFocused: false,
          isFocusVisible: false,
          isIndeterminate: false,
          isReadOnly: false,
          isInvalid: false,
          isRequired: false
        };
        let isFocused = (renderProps.hasSubmenu && renderProps.isOpen) || renderProps.isFocused;
        // virtualized doesnt use subgrid so always render a hidden checkbox for submenu triggers so they stay indented
        let showSelectionIndicator = !renderProps.hasSubmenu || isVirtualized;
        return (
          <>
            <Provider
              values={[
                [
                  IconContext,
                  {
                    slots: {
                      icon: {
                        render: centerBaseline({slot: 'icon', styles: iconCenterWrapper}),
                        styles: icon
                      },
                      descriptor: {render: centerBaseline({slot: 'descriptor', styles: descriptor})} // TODO: remove once we have default?
                    }
                  }
                ],
                [
                  TextContext,
                  {
                    slots: {
                      [DEFAULT_SLOT]: {styles: label({size})},
                      label: {styles: label({size})},
                      description: {styles: description({...renderProps, size, isFocused})},
                      value: {styles: value}
                    }
                  }
                ],
                [KeyboardContext, {styles: keyboard({...renderProps, size, isFocused})}],
                [ImageContext, {styles: image({size})}]
              ]}>
              {renderProps.selectionMode === 'single' && showSelectionIndicator && (
                <CheckmarkIcon
                  size={checkmarkIconSize[size]}
                  className={checkmark({
                    ...renderProps,
                    isSelected: renderProps.isSelected && !renderProps.hasSubmenu,
                    size
                  })}
                />
              )}
              {renderProps.selectionMode === 'multiple' && showSelectionIndicator && (
                <div
                  aria-hidden={renderProps.hasSubmenu || undefined}
                  className={mergeStyles(
                    checkbox,
                    box(checkboxRenderProps),
                    renderProps.hasSubmenu ? hiddenCheckbox : null
                  )}>
                  <CheckmarkIcon size={size} className={iconStyles} />
                </div>
              )}
              {typeof children === 'string' ? <Text slot="label">{children}</Text> : children}
              {isLinkOut && !hideLinkOutIcon && (
                <div slot="descriptor" className={descriptor}>
                  <LinkOutIcon
                    size={linkIconSize[size]}
                    className={style({
                      scaleX: {
                        direction: {
                          rtl: -1
                        }
                      }
                    })({direction})}
                  />
                </div>
              )}
              {renderProps.hasSubmenu &&
                (isUnavailable ? (
                  <UnavailableIconWrapper direction={direction} size={size} id={infoIconId} />
                ) : (
                  <div slot="descriptor" className={descriptor}>
                    <ChevronRightIcon
                      size={size}
                      className={style({
                        scaleX: {
                          direction: {
                            rtl: -1
                          }
                        }
                      })({direction})}
                    />
                  </div>
                ))}
            </Provider>
          </>
        );
      }}
    </AriaMenuItem>
  );
  // oxlint-enable react/react-compiler
}

/**
 * The MenuTrigger serves as a wrapper around a Menu and its associated trigger,
 * linking the Menu's open state with the trigger's press state.
 */
function MenuTrigger(props: MenuTriggerProps): ReactNode {
  // RAC sets isPressed via PressResponder when the menu is open.
  // We don't want press scaling to appear to get "stuck", so override this.
  // For mouse interactions, menus open on press start. When the popover underlay appears
  // it covers the trigger button, causing onPressEnd to fire immediately and no press scaling
  // to occur. We override this by listening for pointerup on the document ourselves.
  let [isPressed, setPressed] = useState(false);
  let {addGlobalListener} = useGlobalListeners();
  let onPressStart = (e: PressEvent) => {
    if (e.pointerType !== 'mouse') {
      return;
    }
    setPressed(true);
    addGlobalListener(
      document,
      'pointerup',
      () => {
        setPressed(false);
      },
      {once: true, capture: true}
    );
  };

  let {align = 'start', direction = 'bottom', shouldFlip, trigger = 'press'} = props;
  let placement: Placement;
  switch (direction) {
    case 'left':
    case 'right':
    case 'start':
    case 'end':
      placement = `${direction} ${align === 'end' ? 'bottom' : 'top'}` as Placement;
      break;
    case 'bottom':
    case 'top':
    default:
      placement = `${direction} ${align}` as Placement;
  }
  let holdAffordance = trigger === 'longPress';
  let actionButtonContext = useSlottedContext(ActionButtonContext) || {};
  let toggleButtonContext = useSlottedContext(ToggleButtonContext) || {};

  return (
    <Provider
      values={[
        [ActionButtonContext, {...actionButtonContext, holdAffordance}],
        [ToggleButtonContext, {...toggleButtonContext, holdAffordance}]
      ]}>
      <InternalMenuTriggerContext.Provider
        value={{
          align: props.align,
          direction: props.direction,
          shouldFlip: props.shouldFlip
        }}>
        <PopoverContext.Provider
          value={{
            hideArrow: true,
            offset: props.trigger === 'contextMenu' ? 0 : 8,
            crossOffset: 0,
            placement,
            shouldFlip
          }}>
          <InPopoverContext.Provider value={false}>
            <AriaMenuTrigger {...props}>
              <PressResponder onPressStart={onPressStart} isPressed={isPressed}>
                {props.children}
              </PressResponder>
            </AriaMenuTrigger>
          </InPopoverContext.Provider>
        </PopoverContext.Provider>
      </InternalMenuTriggerContext.Provider>
    </Provider>
  );
}

export interface SubmenuTriggerProps extends Omit<AriaSubmenuTriggerProps, 'delay'> {}

function SubmenuTrigger(props: SubmenuTriggerProps): JSX.Element {
  // For submenus, the offset from the edge of the popover should be 10px.
  // Subtract 8px for the padding around the parent menu.
  // Offset by padding + border so that the first item in a submenu lines up with the parent menu item.
  return (
    <AriaSubmenuTrigger {...props}>
      {props.children[0]}
      <PopoverContext.Provider
        value={{hideArrow: true, offset: -2, crossOffset: -8, placement: 'end top'}}>
        {props.children[1]}
      </PopoverContext.Provider>
    </AriaSubmenuTrigger>
  );
}

export interface UnavailableMenuItemTriggerProps {
  /**
   * The contents of the UnavailableMenuItemTrigger. The first child should be a MenuItem and the
   * second child be a ContextualHelpPopover.
   */
  children: ReactElement[];
  /**
   * Whether the menu item is currently unavailable.
   *
   * @default false
   */
  isUnavailable?: boolean;
}

function UnavailableMenuItemTrigger(props: UnavailableMenuItemTriggerProps): JSX.Element {
  let {isUnavailable = false, children} = props;
  if (isUnavailable) {
    return (
      <UnavailableContext.Provider value={isUnavailable}>
        <AriaSubmenuTrigger>
          {children[0]}
          <PopoverContext.Provider
            value={{hideArrow: true, offset: -2, crossOffset: -8, placement: 'end top'}}>
            {children[1]}
          </PopoverContext.Provider>
        </AriaSubmenuTrigger>
      </UnavailableContext.Provider>
    );
  }

  return children[0] as JSX.Element;
}

export {MenuTrigger, SubmenuTrigger, UnavailableMenuItemTrigger};

// This is purely so that storybook generates the types for both Menu and MenuTrigger
interface ICombined<T> extends MenuProps<T>, Omit<MenuTriggerProps, 'children'> {}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function CombinedMenu<T>(props: ICombined<T>): ReactNode {
  return <div />;
}
